import { Uploader } from 'plupload'
import { MULTIMEDIA_MAX_FILE_SIZE_BYTES } from '../../../../../../../../config'

export interface FileInputOptions {
  multiple?: boolean
  accept?: string
  onFilesSelected?: (files: FileList) => void
}

export type FilePreparationErrorHandler = (file: File, error: unknown) => void

const SAFARI_WEBKIT_FILE_COPY_MARKER = '__clfSafariWebKitFileCopy'

type MarkedFile = File & { [SAFARI_WEBKIT_FILE_COPY_MARKER]?: boolean }

export const isSafariWebKit = (userAgent: string = navigator.userAgent): boolean =>
  /AppleWebKit/i.test(userAgent) && !/(Android|Chrome|Chromium|Edg|OPR|SamsungBrowser)/i.test(userAgent)

export const hasSafariWebKitFileCopy = (file: any): boolean => {
  const nativeFile = file?.getNative?.() ?? file
  return nativeFile?.[SAFARI_WEBKIT_FILE_COPY_MARKER] === true
}

const createSafeUploadFile = async (file: File): Promise<File> => {
  // Let Plupload reject oversized files through its existing -600 error path.
  // Buffering them first could allocate more than twice their size in Safari.
  if (file.size > MULTIMEDIA_MAX_FILE_SIZE_BYTES) return file

  const safeFile = new File([await file.arrayBuffer()], file.name, {
    lastModified: file.lastModified,
    type: file.type,
  }) as MarkedFile
  Object.defineProperty(safeFile, SAFARI_WEBKIT_FILE_COPY_MARKER, { value: true })
  return safeFile
}

export const addFilesToPlupload = async (
  uploader: Uploader,
  files: FileList,
  onFilePreparationError?: FilePreparationErrorHandler
): Promise<void> => {
  if (!isSafariWebKit()) {
    Array.from(files).forEach((file) => uploader.addFile(file))
    return
  }

  for (const file of Array.from(files)) {
    try {
      const safeFile = await createSafeUploadFile(file)
      uploader.addFile(safeFile)
    } catch (error) {
      onFilePreparationError?.(file, error)
    }
  }
}

export const createAndTriggerFileInput = (options: FileInputOptions = {}): void => {
  const { multiple = true, accept = 'image/*', onFilesSelected } = options

  const fileInput = document.createElement('input')
  fileInput.type = 'file'
  fileInput.multiple = multiple
  fileInput.accept = accept
  fileInput.style.display = 'none'

  let cleanupTimer: number | undefined
  let isRemoved = false
  const cleanup = () => {
    if (isRemoved) return

    isRemoved = true
    if (cleanupTimer !== undefined) window.clearTimeout(cleanupTimer)
    window.removeEventListener('focus', handleWindowFocus)
    document.body.removeChild(fileInput)
  }
  const handleWindowFocus = () => {
    // The file dialog closes before the browser dispatches change. Waiting one
    // tick covers cancellation without removing the input too early in Safari.
    cleanupTimer = window.setTimeout(cleanup, 0)
  }

  fileInput.onchange = (event) => {
    const files = (event.target as HTMLInputElement).files
    if (files && files.length > 0) {
      onFilesSelected?.(files)
    }
    cleanup()
  }

  document.body.appendChild(fileInput)
  window.addEventListener('focus', handleWindowFocus)
  fileInput.click()
}

export const createAndTriggerPluploadFileInput = (
  options: Omit<FileInputOptions, 'onFilesSelected'> = {},
  uploader?: Uploader,
  onFilePreparationError?: FilePreparationErrorHandler
): void => {
  createAndTriggerFileInput({
    ...options,
    onFilesSelected: (files) => {
      if (uploader) {
        void addFilesToPlupload(uploader, files, onFilePreparationError)
      } else if ((window as any).pluploadInstance) {
        Array.from(files).forEach((file) => (window as any).pluploadInstance.addFile(file))
      }
    },
  })
}
